import type { BundledArticle } from '@/types/article'

// Authoring shape: each entry holds markdown in `bodyMd` and an optional
// `quiz`. The seed (app/server/lib/seed.ts) converts these into the runtime
// ArticleBlock[] shape on first boot via markdownToBlocks() — see
// app/src/lib/markdownToBlocks.ts.
export const articles: BundledArticle[] = [
  {
    slug: 'redesigning-informal-markets',
    title: 'Redesigning Informal Markets: The Case for All Artisans',
    date: '2026-03-28',
    tags: ['Platform Thinking', 'African Market Realities'],
    excerpt: 'Why integrating artisans into the digital economy is not just an economic necessity — it is a structural redesign for human dignity and ownership.',
    readTime: 7,
    featured: true,
    bodyMd: `## The Visible and the Invisible

Across Africa, a vast part of our economies remains informal — yet invisible to the digital systems that shape modern commerce. Artisans, craftspeople, and small producers operate outside the channels that finance, logistics, and commerce use to scale.

## More Than Inclusion — Ownership

Integration is not just an economic necessity. It is a structural redesign for human dignity and ownership. Bringing artisans into the digital economy means designing systems where they own their visibility, their customer relationships, and their growth trajectory — not simply appear in someone else's marketplace.

## What the Redesign Looks Like

1. **Identity infrastructure** that recognises informal producers
2. **Payment rails** tuned to micro-transactions and trust-based commerce
3. **Marketplaces** where ownership flows to producers, not aggregators
4. **Policy** that legitimises the informal without formalising it to death

## The Opportunity

When we redesign markets to include all artisans, we do more than grow GDP numbers — we build systems that honour the texture of how African economies actually work.`,
    quiz: [
      {
        question: 'What does the author argue is the primary reason to integrate artisans into the digital economy?',
        options: ['GDP growth', 'Human dignity and ownership', 'Export potential'],
        answer: 1,
      },
    ],
  },
  {
    slug: 'why-zambian-startups-bleed-out',
    title: 'Why Zambian Startups Bleed Out Before Growth — It Is Not the Product',
    date: '2026-03-10',
    tags: ['Founder and Product Notes', 'African Fintech Strategy'],
    excerpt: 'Most startups do not fail from product competition — they fail from cash flow and operational friction. Here is what to do about it.',
    readTime: 6,
    featured: false,
    bodyMd: `## The Diagnosis

Most Zambian startups do not fail because of product. They fail because of cash flow and operational friction.

## Cash Flow First

Before worrying about market fit, most founders should be obsessing about the lag between invoice and payment, between expense and collection, between order and delivery. Every friction point there consumes runway faster than any competitor can.

## Operational Discipline Is the Differentiator

The discipline to collect on time, pay on time, and close the operational loop is not a side effect of success. It is the cause.

## Concrete Moves

- **Tighten the invoice-to-cash cycle** — every day shaved is runway extended
- **Automate recurring payments** — reduce the manual work that kills weekends and margin
- **Measure operational friction weekly** — not just revenue

Growth without operational discipline is a headline. Operational discipline with modest growth is a business.`,
    quiz: [
      {
        question: 'According to the article, what is the most common reason Zambian startups fail?',
        options: ['Product competition', 'Cash flow and operational friction', 'Regulation'],
        answer: 1,
      },
    ],
  },
  {
    slug: 'from-visibility-to-ownership',
    title: 'From Visibility to Ownership: Building the Future Artisan Economy Together',
    date: '2026-02-18',
    tags: ['Platform Thinking', 'African Market Realities'],
    excerpt: 'Visibility is a starting point, not a destination. The future of the artisan economy is about ownership of channels, data, and narrative.',
    readTime: 5,
    featured: false,
    bodyMd: `## Visibility Without Ownership Is a Trap

Putting artisans on marketplaces creates visibility but often strips ownership. The platform owns the customer, the data, the terms of trade.

## What Ownership Looks Like

1. **Direct customer relationships** — the artisan holds the channel
2. **Portable reputation** — ratings and history are owned, not rented
3. **Pricing sovereignty** — producers set terms, not aggregators
4. **Narrative control** — the story of the work belongs to the maker

## Designing for Ownership

We need infrastructure that assumes ownership by default — identity that travels, payments that settle directly, reputations that port across platforms.

## The Shift

The artisan economy of the next decade will not be built by platforms that extract value from informal producers. It will be built by infrastructure that lets informal producers capture value themselves.`,
    quiz: [
      {
        question: 'What does the author identify as the trap of visibility without ownership?',
        options: ['Limited reach', 'Loss of customer relationships, data, and narrative', 'Higher fees'],
        answer: 1,
      },
    ],
  },
  {
    slug: 'the-mobile-money-advantage',
    title: 'The Mobile Money Advantage',
    date: '2026-01-22',
    tags: ['Payment Infrastructure Notes', 'African Fintech Strategy'],
    excerpt: 'Mobile money is not a transitional technology — it is a structural advantage for African economies that the rest of the world is only beginning to understand.',
    readTime: 4,
    featured: false,
    bodyMd: `## Not a Stepping Stone

Mobile money is often treated as a transitional technology — something African economies use on the way to "real" banking. This framing misses the point.

## Structural Advantages

Mobile money provides:
- **Universal access** that legacy banks never achieved
- **Low-cost infrastructure** designed for the economies it serves
- **Trust networks** built on merchant relationships, not branch visits

## What the Rest of the World Is Learning

The features that make mobile money work in Africa — interoperability, low cost, merchant ubiquity — are now the features that fintech innovators in the Global North are trying to build. We got here first.

## Defending the Lead

The question is no longer whether mobile money is viable. It is whether we will retain the structural advantage we have built, or give it away to global payment giants that arrive with capital and APIs but no context.`,
    quiz: [
      {
        question: 'What framing of mobile money does the author argue against?',
        options: ['That it is revolutionary', 'That it is a transitional technology to legacy banking', 'That it is insecure'],
        answer: 1,
      },
    ],
  },
]
